let dropdownLoaded = false

const { getElement } = window.BAO.Utils

class HeaderDropdown extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    this.lastTouched = null
    // In the theme editor the header group is already rendered server-side with
    // the latest settings, so re-fetching is redundant.
    if (window.Shopify?.designMode) {
      dropdownLoaded = true
      return
    }
    if (!dropdownLoaded) this.loadDropdown()
  }

  loadDropdown () {
    const sectionId = this.sectionId
    window.BAO.sectionRenderer
      .fetch(sectionId, `${window.Shopify.routes.root}?view=dropdown`)
      .then(res => {
        dropdownLoaded = true
        const newDOM = window.BAO.sectionRenderer.convertToDOM(res[sectionId])
        const myself = newDOM.querySelector(this.localName)
        if (!myself) return
        this.outerHTML = myself.outerHTML
      })
      .catch(() => {})
  }

  /**
   * The header now lives inside a section group, so its rendered id is dynamic
   * (e.g. "sections--123__header"). Resolve it from the wrapping Shopify section
   * so the dropdown is re-rendered from the same source the theme editor writes
   * to, rather than the legacy standalone "header" section.
   *
   * @returns {string}
   */
  get sectionId () {
    const sectionEl = this.closest('.shopify-section')
    return sectionEl ? sectionEl.id.replace('shopify-section-', '') : 'header'
  }

  setupListeners () {
    super.setupListeners()
    if (this.els?.['menu-dropdown-toggle']?.exists) {
      this.setupMenuDropdownToggleListeners()
      this.setupItemListeners()
    }
    this.setupDocumentListeners()
  }

  setupMenuDropdownToggleListeners () {
    this.els?.['menu-dropdown-toggle']?.elements.forEach(menuToggleEl => {
      this.listeners.add(
        menuToggleEl,
        'click',
        this.handleToggleButtonClick.bind(this),
      )
    })
  }

  setupItemListeners () {
    this.els?.item?.elements.forEach(itemEl => {
      const menuDropdownEl = getElement(
        '[data-header-dropdown-el="menu-dropdown"]',
        { context: itemEl },
      )

      if (!menuDropdownEl) return

      const menuLinkEl = getElement('.hd-Banner_Link', { context: itemEl })

      this.listeners.add(
        menuLinkEl,
        'touchstart',
        this.handleToggleButtonTouchStart,
      )
    })
  }

  setupDocumentListeners () {
    this.listeners.add(document, 'focusin', this.handleFocusIn.bind(this))
    this.listeners.add(document, 'keyup', this.handleEscapePress.bind(this))
  }

  /**
   * @param {TouchEvent} event
   *
   * @returns {void}
   */
  handleToggleButtonTouchStart (event) {
    if (this.lastTouched === this.innerText) return
    event.preventDefault()
    this.lastTouched = this.innerText
  }

  /**
   * @param {ClickEvent} event
   *
   * @returns {void}
   */
  handleToggleButtonClick (event) {
    const menuToggleEl = event.target
    const parentItemEl = menuToggleEl.closest(this.els?.item?.selector)
    if (!parentItemEl) return
    this.toggleDropdown(menuToggleEl, parentItemEl)
  }

  /**
   * @param {HTMLElement} menuToggleEl
   * @param {HTMLElement} parentItemEl
   *
   * @returns {void}
   */
  toggleDropdown (menuToggleEl, parentItemEl) {
    const menuDropdownEl = getElement(this.els?.['menu-dropdown']?.selector, {
      context: parentItemEl,
    })
    const isExpanded = menuDropdownEl.getAttribute('aria-expanded') === 'true'
    if (isExpanded) {
      menuToggleEl.removeAttribute('data-active')
      menuDropdownEl.removeAttribute('aria-expanded')
    } else {
      menuToggleEl.setAttribute('data-active', String(!isExpanded))
      menuDropdownEl.setAttribute('aria-expanded', String(!isExpanded))
    }
  }

  /**
   * @param {FocusInEvent} event
   *
   * @returns {void}
   */
  handleFocusIn (event) {
    if (
      event.target.closest(this.localName) &&
      !event.target.closest('[aria-expanded="true"]')
    ) {
      this.closeDropdowns()
    }

    if (!event.target.closest(this.localName)) {
      this.closeDropdowns()
    }
  }

  /**
   * @param {KeyboardEvent} event
   *
   * @returns {void}
   */
  handleEscapePress (event) {
    if (event.code?.toUpperCase() !== 'ESCAPE') return
    this.closeDropdowns()
  }

  closeDropdowns () {
    if (this.els?.['menu-dropdown-toggle']?.exists) {
      this.els?.['menu-dropdown-toggle']?.elements.forEach(menuDropdownEl => {
        menuDropdownEl.removeAttribute('data-active')
      })
    }

    if (this.els?.['menu-dropdown']?.exists) {
      this.els?.['menu-dropdown']?.elements.forEach(menuDropdownEl => {
        menuDropdownEl.removeAttribute('aria-expanded')
      })
    }
  }
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown)
}